import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for OAuth callback (tokens in URL hash)
    const handleAuthCallback = async () => {
      // Check if there's a hash in the URL (OAuth callback)
      if (window.location.hash && window.location.hash.includes('access_token')) {
        console.log('OAuth callback detected, processing...');
        // Let Supabase handle the hash automatically
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error processing OAuth callback:', error);
        } else if (data.session) {
          console.log('Session established from OAuth callback');
          setSession(data.session);
          setUser(data.session.user);
          // Clean up URL hash
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    };

    // Get initial session
    const initializeAuth = async () => {
      try {
        // First handle any OAuth callback
        await handleAuthCallback();
        
        // Then get the current session
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
        } else {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log('Auth state changed:', event, newSession?.user?.email);
      
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      // Update localStorage for compatibility with existing code
      if (newSession?.user) {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userEmail', newSession.user.email);
        if (newSession.user.user_metadata?.full_name) {
          localStorage.setItem('userName', newSession.user.user_metadata.full_name);
        }
      } else {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const currentUrl = window.location.origin;
    // Use auth callback route to handle the OAuth redirect
    const redirectUrl = `${currentUrl}/auth/callback`;
    
    console.log('Initiating Google OAuth, redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    
    if (error) {
      console.error('Error signing in with Google:', error);
      return { error };
    }
    return { data };
  };

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      console.error('Error signing in with email:', error);
      return { error };
    }
    return { data };
  };

  const signUpWithEmail = async (email, password, metadata = {}) => {
    const currentUrl = window.location.origin;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${currentUrl}/auth/callback`,
      },
    });
    if (error) {
      console.error('Error signing up with email:', error);
      return { error };
    }
    return { data };
  };

  const resetPassword = async (email) => {
    const currentUrl = window.location.origin;
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${currentUrl}/login`,
    });
    if (error) {
      console.error('Error sending password reset:', error);
      return { error };
    }
    return { data };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      return { error };
    }
    // Clear localStorage
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    setUser(null);
    setSession(null);
    return { error: null };
  };

  const value = {
    user,
    session,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    signOut,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
